/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Account Info General Tab component
 *
 * @author Yakov Gushchin
 * @version 1.21.0
 * @since 1.11.0
 */

import { Fragment, memo, useMemo } from 'react';

import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { icons } from '../../../assets';
import { InfoBlock, TextBlock } from '../../../components/common';
import { FlexStyled } from '../../../components/common/Block/Flexbox';
import { ImgStyled } from '../../../components/common/Block/Img';
import ArrowTooltip from '../../../components/common/Tooltip/Tooltip';
import { EColorStatus } from '../../../modules/app/appConstants';
import {
    makeSelectPageAccountInfoTranslation,
    makeSelectTranslationCommon,
} from '../../../modules/internationalization/intSelectors';
import { IRouteParams } from '../../../routes/constants/routesConstants';
import { fonts } from '../../../theme';
import { formatDate } from '../../../utils/formatText';
import { makeSelectAccountInfoGeneralMeta } from '../pageAccountInfoSelectors';
import AccountInfoGeneralGroup from './AccountInfoGeneralGroup';
import {
    AccountInfoGeneralInfoBlockStyled,
    AccountInfoGeneralInfoGroupStyled,
    AccountInfoGeneralStyled,
} from './styled/AccountInfoGeneralStyled';

const AccountInfoGeneral = () => {

    const { clientId } = useParams<IRouteParams>();

    const generalMeta = useSelector(makeSelectAccountInfoGeneralMeta(clientId));

    const { generalInfo: generalTranslation } = useSelector(makeSelectPageAccountInfoTranslation());
    const { status: statusTranslation } = useSelector(makeSelectTranslationCommon());

    const verificationHintMessage = useMemo(() =>
            generalMeta?.verificationResult?.statusDescription
            || (generalMeta?.verificationResult?.statusHintCode
                && (statusTranslation.codes[generalMeta?.verificationResult.statusHintCode]
                    || statusTranslation.defaultMessage)
            ) || '',
        [generalMeta, statusTranslation]);

    if (!generalMeta) return null;

    const {
        // Проверка/блокировка
        verificationResult,
        // Общая информация
        name,
        subdivisionName,
        // Дополнительная информация
        addInfo,
        // Персональный менеджер
        personalManager,
        // Юридическая информация из OAPI
        jurInfo,
        // Данные о компании из SFA
        companyInfo,
    } = generalMeta;

    return (
        <Fragment>
            <AccountInfoGeneralStyled>
                <div>
                    {
                        // Проверка/блокировка
                        !!verificationResult && <Fragment>
                            {
                                // Регистрационные данные
                                (!!verificationResult.state || verificationResult.statusDetails) &&
                                <AccountInfoGeneralInfoGroupStyled>
                                    <TextBlock className="account-info-general__title">
                                        {generalTranslation.fieldVerificationRegistrationData}
                                    </TextBlock>

                                    <FlexStyled alignItems="center">
                                        <TextBlock font={fonts.textMainBold}>
                                            {verificationResult.statusDetails || verificationResult.state?.def}
                                        </TextBlock>
                                        {
                                            verificationHintMessage
                                            && <ArrowTooltip
                                                isHintMessage
                                                message={verificationHintMessage}
                                                placement="top-start"
                                                title={
                                                    verificationResult.statusDetails || verificationResult.state?.def
                                                }
                                            >
                                                <ImgStyled className="small" src={icons.infoBlackSmallIcon}/>
                                            </ArrowTooltip>
                                        }
                                    </FlexStyled>

                                    {
                                        !!verificationResult.verificationDate &&
                                        <TextBlock font={fonts.textSmall}>
                                            {formatDate(verificationResult.verificationDate, { skipZone: true })}
                                        </TextBlock>
                                    }
                                </AccountInfoGeneralInfoGroupStyled>
                            }
                            {
                                // Плановая дата блокировки
                                !!verificationResult.lockingDate &&
                                <AccountInfoGeneralGroup
                                    title={generalTranslation.fieldVerificationDate}
                                    value={formatDate(verificationResult.lockingDate, { skipZone: true })}
                                />
                            }
                        </Fragment>
                    }
                    <AccountInfoGeneralGroup
                        title={generalTranslation.fieldName}
                        value={name}
                    />
                    {
                        // Наименование структурного подразделения
                        !!subdivisionName &&
                        <AccountInfoGeneralGroup
                            title={generalTranslation.fieldSubdivisionName}
                            value={subdivisionName}
                        />
                    }
                    {
                        // Дополнительная информация
                        !!addInfo &&
                        <Fragment>
                            <AccountInfoGeneralGroup
                                title={generalTranslation.fieldAddInfoContract}
                                value={addInfo.contract}
                            />
                            <AccountInfoGeneralGroup
                                title={generalTranslation.fieldAddInfoINN}
                                value={addInfo.inn}
                            />
                            <AccountInfoGeneralGroup
                                title={generalTranslation.fieldAddInfoKPP}
                                value={addInfo.kpp}
                            />
                            <AccountInfoGeneralGroup
                                title={generalTranslation.fieldAddInfoSubsCount}
                                value={addInfo.subsCount}
                            />
                        </Fragment>
                    }
                    {
                        // Персональный менеджер
                        !!personalManager?.info &&
                        <AccountInfoGeneralGroup
                            title={generalTranslation.fieldPersonalManager}
                            value={personalManager.info}
                        />
                    }
                    <AccountInfoGeneralGroup
                        noEllipse
                        noFieldValue={generalTranslation.noFieldValue}
                        title={generalTranslation.fieldJurAddress}
                        value={jurInfo?.address}
                    />
                </div>
                <div>
                    {/* Юридическая информация из OAPI */}
                    <AccountInfoGeneralGroup
                        noFieldValue={generalTranslation.noFieldValue}
                        title={generalTranslation.fieldJurContactName}
                        value={jurInfo?.contactName}
                    />
                    <AccountInfoGeneralGroup
                        noFieldValue={generalTranslation.noFieldValue}
                        title={generalTranslation.fieldJurEmail}
                        value={jurInfo?.email}
                    />
                    <AccountInfoGeneralGroup
                        noFieldValue={generalTranslation.noFieldValue}
                        title={generalTranslation.fieldJurContactPhone}
                        value={jurInfo?.phone}
                    />
                    <AccountInfoGeneralGroup
                        noFieldValue={generalTranslation.noFieldValue}
                        title={generalTranslation.fieldJurLeaderName}
                        value={jurInfo?.leaderName}
                    />
                    {/* Данные о компании из SFA */}
                    <AccountInfoGeneralGroup
                        noFieldValue={generalTranslation.noFieldValue}
                        title={generalTranslation.fieldCompanySectorName}
                        value={companyInfo?.sectorName}
                    />
                    {
                        // Количество сотрудников
                        companyInfo?.staffCountAvailable &&
                        <AccountInfoGeneralGroup
                            noFieldValue={generalTranslation.noFieldValue}
                            title={generalTranslation.fieldCompanyStaffCount}
                            value={companyInfo?.staffCount}
                        />
                    }
                </div>
            </AccountInfoGeneralStyled>
            <AccountInfoGeneralInfoBlockStyled>
                <InfoBlock colorStatus={EColorStatus.WARNING}>
                    {generalTranslation.warningInfo}
                </InfoBlock>
            </AccountInfoGeneralInfoBlockStyled>
        </Fragment>
    );
};

export default memo(AccountInfoGeneral);
